import { useState } from "react";
import axios from "axios";
import { X, Download, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CatalogModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        source: "catalog_download",
      });
      setIsSuccess(true);
      
      // Trigger download (placeholder PDF)
      setTimeout(() => {
        const link = document.createElement("a");
        link.href = "/catalog-placeholder.pdf";
        link.download = "Reliable-Exports-Catalog.pdf";
        link.click();
      }, 500);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: "", email: "", company: "", phone: "" });
    setIsSuccess(false);
    setError("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        data-testid="catalog-modal"
        className="bg-cream sm:max-w-[480px] border-sand/20"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-forest">
            Download Product Catalog
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {isSuccess
              ? "Thank you! Your download will begin shortly."
              : "Please provide your details to download our complete product catalog."}
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Download className="w-8 h-8 text-forest" />
            </div>
            <p className="text-forest font-medium mb-2">Download Started!</p>
            <p className="text-sm text-muted-foreground">
              If the download doesn't start, click{" "}
              <a href="/catalog-placeholder.pdf" className="text-sand hover:underline">
                here
              </a>
            </p>
            <Button
              onClick={handleClose}
              className="mt-6 bg-forest hover:bg-forest-dark text-white rounded-full px-8"
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-forest">
                Full Name *
              </Label>
              <Input
                id="name"
                name="name"
                data-testid="catalog-name-input"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="John Doe"
                className="bg-white border-sand/30 focus:border-sand focus:ring-sand"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-forest">
                Email Address *
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                data-testid="catalog-email-input"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john@company.com"
                className="bg-white border-sand/30 focus:border-sand focus:ring-sand"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company" className="text-forest">
                Company Name
              </Label>
              <Input
                id="company"
                name="company"
                data-testid="catalog-company-input"
                value={formData.company}
                onChange={handleChange}
                placeholder="Your Company"
                className="bg-white border-sand/30 focus:border-sand focus:ring-sand"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-forest">
                Phone Number
              </Label>
              <Input
                id="phone"
                name="phone"
                data-testid="catalog-phone-input"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+1 234 567 890"
                className="bg-white border-sand/30 focus:border-sand focus:ring-sand"
              />
            </div>

            {error && (
              <p data-testid="catalog-error" className="text-red-500 text-sm">
                {error}
              </p>
            )}

            <Button
              type="submit"
              data-testid="catalog-submit-btn"
              disabled={isSubmitting}
              className="w-full bg-sand hover:bg-sand-dark text-white font-medium rounded-full py-6 transition-all duration-300"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download Catalog
                </>
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
